import React, { FC } from "react";
import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Calendar4Event,
  Check,
  Clock,
  Envelope,
  GeoAlt,
  HourglassSplit,
  Person,
  Telephone,
  TelephoneOutbound,
  X,
  XLg,
  CardText,
  PersonSlash,
  Hourglass,
  InfoCircle,
} from "react-bootstrap-icons";
import moment from "moment";
import { useUserQuery } from "../../graphql/user.hooks";
import {
  BookingQuery,
  useCancelReservationMutation,
} from "../../graphql/booking.hooks";
import LoadingIcon from "../../pages/reservation/images/loading.svg";
import NoRestaurantLogo from "../../pages/reservation/images/no-restaurant-logo.png";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { ApolloQueryResult } from "@apollo/client";

type ReservationItem = NonNullable<
  BookingQuery["user"]["reservations"]["items"]
>[number];

type Props = {
  showModal: boolean;
  onCancel?: () => void;
  reservations: ReservationItem[];
  uuid: string;
  refetch?: () => Promise<ApolloQueryResult<BookingQuery>>;
};

export const BookingDetailsModal: FC<Props> = ({
  showModal,
  onCancel,
  reservations,
  uuid,
  refetch,
}) => {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const currentReservation = reservations?.find(
    (reservation) => reservation.uuid === uuid
  );
  const [cancelReservation, { loading: deleteLoading }] =
    useCancelReservationMutation();
  const { data: userData } = useUserQuery();

  const tryCancelReservation = async () => {
    if (!uuid) return;

    try {
      await cancelReservation({
        variables: {
          uuid,
        },
      });

      await refetch?.();
      onCancel?.();
    } catch (err) {
      console.error(err);
    }
  };

  const isBeforeReservation = moment().isBefore(
    moment(currentReservation?.reservationAt)
  );

  const cancelableStatuses = currentReservation?.isPremium
    ? ["WAITING_FOR_OPENING", "PENDING", "IN_PROGRESS", "CONFIRMED"]
    : ["WAITING_FOR_OPENING", "PENDING"];

  const canCancel =
    cancelableStatuses.includes(currentReservation?.status ?? "") &&
    (currentReservation?.status !== "CONFIRMED" || isBeforeReservation);

  const canCancelWithPremium =
    ["WAITING_FOR_OPENING", "PENDING", "IN_PROGRESS"].includes(
      currentReservation?.status ?? ""
    ) ||
    (currentReservation?.status === "CONFIRMED" && isBeforeReservation);

  const premCancelationEvent = () => {
    if (window.gtag) {
      window.gtag("event", "prem_cancelation_available_on_premium");
    }
  };

  return (
    <Modal show={showModal} onHide={onCancel} centered scrollable>
      <Modal.Header className="border-0" closeButton>
        <Modal.Title className="text-dark">
          {t("booking.bookDetails")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-dark px-4">
        <div
          className="overflow-hidden rounded-4 mb-4"
          style={{
            height: "183px",
          }}
        >
          <img
            className="w-100 h-100"
            loading="lazy"
            style={{
              objectFit: "cover",
              objectPosition: "center",
            }}
            src={currentReservation?.restaurant?.logo || NoRestaurantLogo}
            alt={`Img-${
              currentReservation?.restaurant?.name ||
              currentReservation?.customPlace?.name
            }`}
          />
        </div>
        <h4 className="mb-1">
          {currentReservation?.restaurant?.name ||
            currentReservation?.customPlace?.name}
        </h4>

        <p className="text-gray">
          {currentReservation?.restaurant?.cuisines.map((cuisine, index) => (
            <span key={cuisine.id}>
              {cuisine.name}
              {index < currentReservation?.restaurant?.cuisines.length - 1 &&
                " / "}
            </span>
          ))}
          {currentReservation?.restaurant?.cuisines.length > 0 &&
          currentReservation?.restaurant?.types.length > 0
            ? " • "
            : ""}
          {currentReservation?.restaurant?.types.map((type, index) => (
            <span key={type.id}>
              {type.name}
              {index < currentReservation?.restaurant?.types.length - 1 &&
                " / "}
            </span>
          ))}
        </p>
        <div className="d-flex">
          <span className="me-2">
            <GeoAlt width={16} />
          </span>
          <span>
            {" "}
            {currentReservation?.restaurant?.fullAddress ||
              currentReservation?.customPlace?.location}
          </span>
        </div>
        <hr />
        <div className="d-flex align-items-center mb-3">
          <Calendar4Event className="me-2" />
          <span>
            {moment(currentReservation?.reservationAt).format(
              "dddd, D MMMM, YYYY"
            )}
          </span>
        </div>
        <div className="d-flex align-items-center mb-3">
          <Clock className="me-2" />
          <span>
            {moment(currentReservation?.reservationAt).format("h:mm A")}
          </span>
          {currentReservation?.flexibility !== "NONE" && (
            <span className="ms-2 d-inline-block px-2 rounded bg-primary-light text-primary">
              ± {currentReservation?.flexibility.slice(5, 6)}
            </span>
          )}
        </div>
        <div className="d-flex align-items-center">
          <Person className="me-2" />
          <span> {currentReservation?.guestsCount}</span>
        </div>
        <hr />
        <div className="d-flex align-items-center mb-3">
          <Envelope className="me-2" />
          <span> {userData?.user?.email}</span>
        </div>
        <div className="d-flex align-items-center mb-3">
          <Telephone className="me-2" />
          <span> {userData?.user?.phone}</span>
        </div>
        <div className="d-flex align-items-center">
          <CardText className="me-2" />
          <span>
            {" "}
            {currentReservation?.comment ? currentReservation?.comment : "–"}
          </span>
        </div>
        <hr />
        <div className="mb-2">
          {currentReservation?.status === "PENDING" && (
            <span className="text-blue">
              <HourglassSplit className="me-2 mb-1" />
              {t("booking.pending")}
            </span>
          )}
          {currentReservation?.status === "WAITING_FOR_OPENING" && (
            <span className="text-secondary">
              <Hourglass className="me-2 mb-1" />
              {t("booking.waitingForOpening")}
            </span>
          )}
          {currentReservation?.status === "IN_PROGRESS" && (
            <span className="text-purple">
              <img src={LoadingIcon} alt="LoadingIcon" className="me-2 mb-1" />
              {t("booking.reservationInProgress")}
            </span>
          )}
          {currentReservation?.status === "CONFIRMED" && (
            <span className="text-success">
              <Check className="me-2 mb-1" />
              {t("booking.reservationConfirmed")}
            </span>
          )}
          {currentReservation?.status === "NO_CONNECTION" && (
            <span className="text-warning">
              <TelephoneOutbound className="me-2 mb-1" />
              {t("booking.reservationUnreachable")}
            </span>
          )}
          {currentReservation?.status === "REJECTED" && (
            <span className="text-primary">
              <X className="me-2 mb-1" />
              {t("booking.reservationReject")}
            </span>
          )}
          {currentReservation?.status === "CANCELLED_BY_USER" && (
            <span className="text-dark text-nowrap">
              <PersonSlash className="me-2 mb-1" />
              {t("booking.reservationCanceled")}
            </span>
          )}
        </div>
        {currentReservation?.moderatorComment && (
          <span className="ps-4">{currentReservation?.moderatorComment}</span>
        )}
      </Modal.Body>
      <Modal.Footer className="d-block m-0 border-0 p-0 sticky-bottom bg-white w-100 px-4 pb-4">
        <Row className="gx-0 m-0">
          <Col xs={12} sm={6} className="pe-lg-1 mb-3 mb-lg-0">
            <Button
              variant="outline-primary"
              className="w-100 py-3"
              onClick={onCancel}
            >
              {t("review.modalCancel")}
            </Button>
          </Col>
          <Col xs={12} sm={6} className="ps-lg-1">
            <Button
              as="a"
              href={currentReservation?.restaurant?.link}
              variant="primary"
              className="w-100 fw-bold text-white py-3"
            >
              {t("booking.rebook")}
            </Button>
          </Col>
        </Row>
        {canCancelWithPremium && (
          <div className="justify-content-center align-items-center pt-3 d-flex m-0">
            <Button
              variant="link"
              className="text-decoration-none text-secondary"
              disabled={deleteLoading || !canCancel}
              onClick={tryCancelReservation}
            >
              <XLg className="me-2 mb-1" />
              {t("booking.cancel")}
              {deleteLoading && (
                <Spinner animation="border" role="status" className="ms-2">
                  <span className="visually-hidden">
                    {t("profile.loading")}...
                  </span>
                </Spinner>
              )}
            </Button>
            {!canCancel && (
              <span className="text-primary bg-primary-light rounded-3 px-2 align-items-center justify-content-center d-inline-flex h-75 py-1 fs-7">
                <InfoCircle className="me-2" />
                {t("booking.available")}&nbsp;
                <Link to={`${lng}/payment`} onClick={premCancelationEvent}>
                  {t("booking.premium")}
                </Link>
              </span>
            )}
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};
